import {
  combineLatest,
  defer,
  fromEvent,
  merge,
  Observable,
  Subject,
} from 'rxjs'
import { Storage } from 'infra/storage'
import { switchMap, map, distinctUntilChanged, filter } from 'rxjs/operators'
import { shareLatest } from 'utils/rx-util'

/**
 * Maintains list of all available hardware devices
 * used for communication and caches device choices
 * for future user.
 */
export default class CallDeviceManager {
  private readonly mediaDeviceChanged = new Subject<MediaDeviceInfo>()

  readonly mediaDeviceChanged$ = this.mediaDeviceChanged.asObservable()

  /**
   * The list of available devices.
   */
  readonly mediaDevices$: Observable<MediaDeviceInfo[]>

  readonly selectedAudioInputDevice$: Observable<MediaDeviceInfo | null>
  // readonly selectedAudioOutputDevice$: Observable<MediaDeviceInfo | null>
  readonly selectedVideoInputDevice$: Observable<MediaDeviceInfo | null>

  /**
   * The selected media devices (audio I/O, video).
   */
  readonly selectedDevices$: Observable<Array<MediaDeviceInfo | null>>

  constructor(private localStorage: Storage) {
    // Stream of the currently available hardware devices.
    this.mediaDevices$ = merge(
      defer(() => getDeviceMedia()),
      availableMediaDevicesChangeStream()
    ).pipe(shareLatest())

    this.selectedAudioInputDevice$ = this.selectedDeviceStream('audioinput')
    // this.selectedAudioOutputDevice$ = this.selectedDeviceStream('audiooutput')
    this.selectedVideoInputDevice$ = this.selectedDeviceStream('videoinput')

    // Stream of the currently selected hardware devices
    this.selectedDevices$ = combineLatest([
      this.selectedAudioInputDevice$,
      // this.selectedAudioOutputDevice$,
      this.selectedVideoInputDevice$,
    ]).pipe(shareLatest())

    this.mediaDeviceChanged.subscribe((device) => {
      if (!device) {
        return
      }
      switch (device.kind) {
        case 'audioinput':
          this.localStorage.setItem('audioIn', device.deviceId)
          break
        case 'audiooutput':
        case 'videoinput':
          this.localStorage.setItem('videoIn', device.deviceId)
          break
        default:
          console.error('Something went wrong here...')
      }
    })
  }

  async activate() {
    await getDeviceMedia()
  }

  /**
   * When a new device is selected
   */
  selectMediaDevice(device?: MediaDeviceInfo | null) {
    if (!device) {
      return
    }
    this.mediaDeviceChanged.next(device)
  }

  private loadPreviousDevices(kind: MediaDeviceInfo['kind']) {
    switch (kind) {
      case 'audioinput':
        return this.localStorage.getItem('audioIn')
      case 'videoinput':
        return this.localStorage.getItem('videoIn')
      default:
        return null
    }
  }

  /**
   * Returns the device id for a previously selected device
   * of the given kind
   * @param kind
   * @returns
   */
  public getPreviousDevice(kind: MediaDeviceInfo['kind']) {
    switch (kind) {
      case 'audioinput':
        return this.localStorage.getItem('audioIn')
      case 'videoinput':
        return this.localStorage.getItem('videoIn')
      case 'audiooutput':
        return this.localStorage.getItem('audioOut')
      default:
        return null
    }
  }

  public persistPreviousDevice(device: MediaDeviceInfo) {
    switch (device.kind) {
      case 'audioinput':
        this.localStorage.setItem('audioIn', device.deviceId)
        break
      case 'audiooutput':
        this.localStorage.setItem('audioOut', device.deviceId)
        break
      case 'videoinput':
        this.localStorage.setItem('videoIn', device.deviceId)
        break
      default:
        console.error('Something went wrong here...')
    }
  }

  /**
   * Streams the selected device
   */
  private selectedDeviceStream(
    kind: MediaDeviceInfo['kind']
  ): Observable<MediaDeviceInfo | null> {
    // Attempt to derive a user's previous device selection
    const previousDeviceStream = (
      kind: MediaDeviceInfo['kind']
    ): Observable<MediaDeviceInfo | null> => {
      const previousDeviceId = this.loadPreviousDevices(kind)
      return this.mediaDevices$.pipe(
        map(
          (devices) =>
            devices.find(
              (d) => d.kind === kind && d.deviceId === previousDeviceId
            ) ?? null
        )
      )
    }

    return combineLatest([
      this.mediaDevices$,
      // Take in either a previous device or a new one.
      merge(
        previousDeviceStream(kind),
        this.mediaDeviceChanged$.pipe(filter((d) => d.kind === kind))
      ),
    ]).pipe(
      map(([availableDevices, selectedDevice]) => {
        //  If the selected device is found, then let's use it
        const isSelectedAvailable = availableDevices.some(
          (d) => selectedDevice === d
        )
        if (isSelectedAvailable) {
          return selectedDevice
        } else {
          // Otherwise, attempt to fallback onto a default device.
          // NOTE: Spec states that the default device of any kind will
          // be returned first in the list so this should be safe.
          return availableDevices.find((d) => d.kind === kind) ?? null
        }
      }),
      shareLatest(),
      distinctUntilChanged()
    )
  }
}

// Stream for when devices are added/removed from user's system
function availableMediaDevicesChangeStream() {
  return fromEvent(navigator.mediaDevices, 'devicechange').pipe(
    switchMap(() => getDeviceMedia())
  )
}

async function getDeviceMedia() {
  return navigator.mediaDevices.enumerateDevices()
}
